import LiveKitConnection from './LiveKitConnection';

// Publishes one stream (a camera, a screen share or an extra camera) as its own LiveKit participant, whose
// identity is the stream id. Same interface as OmeSender.
export default class LiveKitSender {
	// getToken: async () => publish token for this stream.
	constructor(url, getToken, stream, { screen = false, onConnected, onClose } = {}) {
		this._url = url;
		this._getToken = getToken;
		this._stream = stream;
		this._screen = screen;
		this._onConnected = onConnected;
		this._onClose = onClose;
		this._connection = null;
		this._senders = {};
		this._stopped = false;
	}

	start() {
		this._stopped = false;
		this._connect();
	}

	async _connect() {
		let connection = null;
		try {
			const token = await this._getToken();
			if(this._stopped) return;
			connection = new LiveKitConnection(this._url, {
				onClose: () => {
					if(this._connection !== connection) return;
					this._connection = null;
					this._senders = {};
					if(!this._stopped) this._onClose?.();
				},
			});
			this._connection = connection;
			await connection.connect(token);
			this._senders = await connection.publish(this._stream, { screen: this._screen });
			if(!this._stopped) this._onConnected?.();
		} catch(e) {
			console.error('LiveKit: could not publish', e);
			// A connection that closed itself has already reported it through its onClose.
			if(connection && this._connection !== connection) return;
			this._connection = null;
			this._senders = {};
			connection?.close();
			if(!this._stopped) this._onClose?.();
		}
	}

	// Swaps the published tracks without dropping the session (see OmeSender.replaceStream).
	// Returns false when the session can't absorb the new stream and has to be restarted.
	async replaceStream(stream) {
		this._stream = stream;
		if(!this._connection) return false;
		for(const kind of [ 'audio', 'video' ]) {
			const track = stream.getTracks().find(t => t.kind === kind) || null;
			const sender = this._senders[kind];
			if(!sender) {
				if(track) return false;
				continue;
			}
			if(track && kind === 'video') track.contentHint = 'motion';
			await sender.replaceTrack(track);
		}
		return true;
	}

	stop() {
		this._stopped = true;
		const connection = this._connection;
		this._connection = null;
		this._senders = {};
		connection?.close();
	}
}
